import { useState } from "react";
import { Settings, Plus, Pencil, Trash2, ChevronRight, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AVAILABLE_ICONS = ["Dumbbell", "Stethoscope", "Store", "Sparkles", "Home",
  "GraduationCap", "Church", "Building", "Heart", "Star", "Users", "Briefcase", "Coffee", "ShoppingBag", "Utensils"];

export function ManageTargetsDialog(props) {
  const { targets, onAddTarget, onUpdateTarget, onDeleteTarget, onAddSubtype,
    onUpdateSubtype, onDeleteSubtype, getSubtypesForTarget, isGlobalTarget, isGlobalSubtype } = props;
  const [open, setOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [newTargetName, setNewTargetName] = useState("");
  const [newTargetIcon, setNewTargetIcon] = useState("Store");
  const [newSubtypeName, setNewSubtypeName] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const addTarget = async () => {
    if (!newTargetName.trim()) return;
    await onAddTarget(newTargetName.trim(), newTargetIcon);
    setNewTargetName(""); setNewTargetIcon("Store");
  };
  const saveTarget = async () => {
    if (!editingTarget?.name.trim()) return;
    await onUpdateTarget(editingTarget.id, editingTarget.name, editingTarget.icon);
    setEditingTarget(null);
  };
  const addSubtype = async () => {
    if (!selectedTarget || !newSubtypeName.trim()) return;
    await onAddSubtype(selectedTarget.id, newSubtypeName.trim());
    setNewSubtypeName("");
  };
  const saveSubtype = async () => {
    if (!editingSubtype?.name.trim()) return;
    await onUpdateSubtype(editingSubtype.id, editingSubtype.name);
    setEditingSubtype(null);
  };
  const doDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "target") {
      await onDeleteTarget(deleteConfirm.id);
      if (selectedTarget?.id === deleteConfirm.id) setSelectedTarget(null);
    } else await onDeleteSubtype(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="manage-targets-button">
            <Settings className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Targets</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Target Categories</h3>
              <div className="flex gap-2">
                <Input placeholder="New target name" value={newTargetName} data-testid="new-target-name"
                  onChange={(e) => setNewTargetName(e.target.value)} className="flex-1" />
                <Select value={newTargetIcon} onValueChange={setNewTargetIcon}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>{AVAILABLE_ICONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="icon" onClick={addTarget} data-testid="add-target-button"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {targets.map((target) => (
                  <div key={target.id}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer ${selectedTarget?.id === target.id ? "bg-primary/10" : "hover:bg-secondary"}`}
                    onClick={() => setSelectedTarget(target)}>
                    {editingTarget?.id === target.id ? (
                      <div className="flex gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
                        <Input value={editingTarget.name} onChange={(e) => setEditingTarget({ ...editingTarget, name: e.target.value })} className="h-7 text-sm" />
                        <Button size="sm" variant="ghost" onClick={saveTarget}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTarget(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm flex items-center gap-2">
                          {target.name}
                          {isGlobalTarget(target) && <Lock className="h-3 w-3 text-muted-foreground" />}
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                        </span>
                        {!isGlobalTarget(target) && (
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`edit-target-${target.id}`} onClick={() => setEditingTarget(target)}><Pencil className="h-3 w-3" /></Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" data-testid={`delete-target-${target.id}`} onClick={() => setDeleteConfirm({ type: "target", id: target.id })}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Sub-Types {selectedTarget && `for ${selectedTarget.name}`}</h3>
              {selectedTarget ? (
                <>
                  <div className="flex gap-2">
                    <Input placeholder="New sub-type name" value={newSubtypeName} data-testid="new-subtype-name"
                      onChange={(e) => setNewSubtypeName(e.target.value)} className="flex-1" />
                    <Button size="icon" onClick={addSubtype} data-testid="add-subtype-button"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto">
                    {getSubtypesForTarget(selectedTarget.id).map((subtype) => (
                      <div key={subtype.id} className="flex items-center justify-between p-2 rounded hover:bg-secondary">
                        {editingSubtype?.id === subtype.id ? (
                          <div className="flex gap-1 flex-1">
                            <Input value={editingSubtype.name} onChange={(e) => setEditingSubtype({ ...editingSubtype, name: e.target.value })} className="h-7 text-sm" />
                            <Button size="sm" variant="ghost" onClick={saveSubtype}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingSubtype(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <>
                            <span className="text-sm flex items-center gap-2">
                              {subtype.name}
                              {isGlobalSubtype(subtype) && <Lock className="h-3 w-3 text-muted-foreground" />}
                            </span>
                            {!isGlobalSubtype(subtype) && (
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`edit-subtype-${subtype.id}`} onClick={() => setEditingSubtype(subtype)}><Pencil className="h-3 w-3" /></Button>
                                <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" data-testid={`delete-subtype-${subtype.id}`} onClick={() => setDeleteConfirm({ type: "subtype", id: subtype.id })}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">Select a target to manage its sub-types</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === "target"
                ? "This will delete the target and all its sub-types and associated leads."
                : "This will delete the sub-type."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
